"use strict";

const { CURRENT_SCHEDULE_YEAR } = require("../config/scheduleArchive");
const { validateJstDateTime } = require("../config/scheduleDateTime");

const SCHEDULE_ID_PATTERN =
  /^schedule-[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ORGANIZER_TYPES = new Set(["Organization", "Person"]);
const JAPANESE_WEEKDAYS = Object.freeze([
  "日",
  "月",
  "火",
  "水",
  "木",
  "金",
  "土",
]);

const requireNonEmptyString = (value, label) => {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Missing or invalid ${label}.`);
  }

  return value.trim();
};

const validateHttpsUrl = (value, label) => {
  const urlValue = requireNonEmptyString(value, label);
  let url;

  try {
    url = new URL(urlValue);
  } catch (error) {
    throw new Error(`Invalid ${label}.`);
  }

  if (url.protocol !== "https:") {
    throw new Error(`${label} must use HTTPS.`);
  }

  return url.href;
};

const parseJstDateTimeParts = (value, label) => {
  const dateTime = validateJstDateTime(value, label);

  return Object.freeze({
    year: Number(dateTime.slice(0, 4)),
    month: Number(dateTime.slice(5, 7)),
    day: Number(dateTime.slice(8, 10)),
    hour: dateTime.slice(11, 13),
    minute: dateTime.slice(14, 16),
  });
};

const formatScheduleOccurrenceDate = (
  occurrence,
  previousOccurrence = null
) => {
  if (!occurrence || typeof occurrence !== "object") {
    throw new Error("Missing schedule occurrence to format.");
  }
  if (typeof occurrence.isHoliday !== "boolean") {
    throw new Error("Schedule occurrence isHoliday must be a boolean.");
  }

  const parts = parseJstDateTimeParts(
    occurrence.startDate,
    "schedule occurrence startDate"
  );
  const weekday = JAPANESE_WEEKDAYS[
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
  ];
  const previousParts = previousOccurrence
    ? parseJstDateTimeParts(
        previousOccurrence.startDate,
        "previous schedule occurrence startDate"
      )
    : null;
  let date = `${parts.year}年${parts.month}月${parts.day}日`;
  if (previousParts && previousParts.year === parts.year) {
    date =
      previousParts.month === parts.month
        ? `${parts.day}日`
        : `${parts.month}月${parts.day}日`;
  }
  const holidayLabel = occurrence.isHoliday ? "・祝" : "";

  return `${date}（${weekday}${holidayLabel}）`;
};

const formatScheduleTimeLabel = (occurrence, occurrenceCount = 1) => {
  if (!occurrence || typeof occurrence !== "object") {
    throw new Error("Missing schedule occurrence time to format.");
  }

  const start = parseJstDateTimeParts(
    occurrence.startDate,
    "schedule occurrence startDate"
  );
  const doors = parseJstDateTimeParts(
    occurrence.doorTime,
    "schedule occurrence doorTime"
  );

  const sharedLabel = occurrenceCount === 2
    ? "両日とも　"
    : occurrenceCount > 2
      ? "各日　"
      : "";
  let endLabel = "";

  if (occurrence.endDate) {
    const end = parseJstDateTimeParts(
      occurrence.endDate,
      "schedule occurrence endDate"
    );
    const endsOnStartDate =
      end.year === start.year &&
      end.month === start.month &&
      end.day === start.day;
    const endDateLabel = endsOnStartDate
      ? ""
      : `${end.month}月${end.day}日 `;

    endLabel = `／終演予定${endDateLabel}${end.hour}:${end.minute}`;
  }

  return `${sharedLabel}開場${doors.hour}:${doors.minute}／開演${start.hour}:${start.minute}${endLabel}`;
};

const formatScheduleLocationLabel = (location) => {
  if (!location || typeof location !== "object") {
    throw new Error("Missing schedule location to format.");
  }

  const name = requireNonEmptyString(location.name, "schedule location name");
  const address = location.address;
  if (!address || typeof address !== "object") {
    throw new Error("Missing schedule address to format.");
  }

  const addressLabel = [
    requireNonEmptyString(address.addressRegion, "schedule address region"),
    requireNonEmptyString(address.addressLocality, "schedule address locality"),
    requireNonEmptyString(address.streetAddress, "schedule street address"),
  ].join("");

  return `${name}（${addressLabel}）`;
};

const validateLocation = (location, occurrenceId) => {
  if (!location || typeof location !== "object") {
    throw new Error(`Missing location for ${occurrenceId}.`);
  }
  if (location["@type"] !== "Place") {
    throw new Error(`Location must be a Place for ${occurrenceId}.`);
  }

  requireNonEmptyString(location.name, `location name for ${occurrenceId}`);

  const address = location.address;
  if (!address || address["@type"] !== "PostalAddress") {
    throw new Error(`Location address must be a PostalAddress for ${occurrenceId}.`);
  }

  ["streetAddress", "addressLocality", "addressRegion"].forEach((field) => {
    requireNonEmptyString(address[field], `${field} for ${occurrenceId}`);
  });
  if (address.addressCountry !== "JP") {
    throw new Error(`addressCountry must be JP for ${occurrenceId}.`);
  }
  if (location.accessNote !== undefined) {
    requireNonEmptyString(
      location.accessNote,
      `location accessNote for ${occurrenceId}`
    );
  }
};

const getLocationIdentity = (location) => {
  const address = location.address;

  return [
    location["@type"],
    location.name,
    address["@type"],
    address.postalCode || "",
    address.addressRegion,
    address.addressLocality,
    address.streetAddress,
    address.addressCountry,
  ].join("\u001f");
};

const validateOrganizers = (organizers, eventId) => {
  if (organizers === undefined) {
    return;
  }
  if (!Array.isArray(organizers) || !organizers.length) {
    throw new Error(`Missing organizers for ${eventId}.`);
  }

  const organizerIdentities = new Set();

  organizers.forEach((organizer) => {
    if (!organizer || typeof organizer !== "object") {
      throw new Error(`Invalid organizer for ${eventId}.`);
    }

    const type = requireNonEmptyString(
      organizer.type,
      `organizer type for ${eventId}`
    );
    const name = requireNonEmptyString(
      organizer.name,
      `organizer name for ${eventId}`
    );
    const url = organizer.url === undefined
      ? ""
      : validateHttpsUrl(organizer.url, `organizer URL for ${eventId}`);
    const identity = [type, name, url].join("\u001f");

    if (!ORGANIZER_TYPES.has(type)) {
      throw new Error(`Invalid organizer type for ${eventId}.`);
    }
    if (organizerIdentities.has(identity)) {
      throw new Error(`Duplicate organizer for ${eventId}.`);
    }
    organizerIdentities.add(identity);
  });
};

const validateTicketOffers = (ticketOffers, eventId) => {
  if (ticketOffers === undefined) {
    return;
  }
  if (!Array.isArray(ticketOffers) || !ticketOffers.length) {
    throw new Error(`Missing ticket offers for ${eventId}.`);
  }

  const offerNames = new Set();

  ticketOffers.forEach((offer) => {
    if (!offer || typeof offer !== "object") {
      throw new Error(`Invalid ticket offer for ${eventId}.`);
    }

    const name = requireNonEmptyString(
      offer.name,
      `ticket offer name for ${eventId}`
    );

    if (offerNames.has(name)) {
      throw new Error(`Duplicate ticket offer name for ${eventId}.`);
    }
    offerNames.add(name);

    if (
      typeof offer.price !== "number" ||
      !Number.isFinite(offer.price) ||
      offer.price < 0
    ) {
      throw new Error(`Invalid ticket offer price for ${eventId}.`);
    }
    if (offer.priceCurrency !== "JPY") {
      throw new Error(`Ticket offer currency must be JPY for ${eventId}.`);
    }
    if (offer.validFrom !== undefined) {
      validateJstDateTime(
        offer.validFrom,
        `ticket offer validFrom for ${eventId}`
      );
    }
  });
};

const validateScheduleEvents = (events) => {
  if (!Array.isArray(events) || !events.length) {
    throw new Error("Schedule events must contain at least one card.");
  }

  const cardIds = new Set();
  const occurrenceIds = new Set();
  let previousCardStartDate = null;

  events.forEach((event, eventIndex) => {
    if (!event || typeof event !== "object") {
      throw new Error(`Invalid schedule card at position ${eventIndex + 1}.`);
    }
    if (!SCHEDULE_ID_PATTERN.test(event.id)) {
      throw new Error(`Invalid schedule card id: ${event.id}`);
    }
    if (cardIds.has(event.id) || occurrenceIds.has(event.id)) {
      throw new Error(`Duplicate schedule card id: ${event.id}`);
    }
    cardIds.add(event.id);

    ["name", "title", "description", "ticket", "detailsHtml"].forEach((field) => {
      requireNonEmptyString(event[field], `${field} for ${event.id}`);
    });
    ["displayTitle", "displaySubtitle", "commentHtml"].forEach((field) => {
      if (event[field] !== undefined) {
        requireNonEmptyString(event[field], `${field} for ${event.id}`);
      }
    });

    if (
      typeof event.ticketToContact !== "boolean" ||
      typeof event.isOver !== "boolean"
    ) {
      throw new Error(`Invalid display state for schedule card: ${event.id}`);
    }
    if (
      !Array.isArray(event.imageKeys) ||
      !event.imageKeys.length ||
      new Set(event.imageKeys).size !== event.imageKeys.length
    ) {
      throw new Error(`Invalid image keys for ${event.id}.`);
    }
    event.imageKeys.forEach((imageKey) => {
      requireNonEmptyString(imageKey, `image key for ${event.id}`);
    });
    validateOrganizers(event.organizers, event.id);
    validateTicketOffers(event.ticketOffers, event.id);

    if (!Array.isArray(event.occurrences) || !event.occurrences.length) {
      throw new Error(`Missing occurrences for ${event.id}.`);
    }

    let previousOccurrenceStartDate = null;
    let sharedStartTime = null;
    let sharedDoorTime = null;
    let sharedDuration = null;
    let sharedLocation = null;
    event.occurrences.forEach((occurrence) => {
      if (!occurrence || typeof occurrence !== "object") {
        throw new Error(`Invalid occurrence for ${event.id}.`);
      }
      if (!SCHEDULE_ID_PATTERN.test(occurrence.id)) {
        throw new Error(`Invalid schedule occurrence id: ${occurrence.id}`);
      }
      if (occurrenceIds.has(occurrence.id) || cardIds.has(occurrence.id)) {
        throw new Error(`Duplicate schedule occurrence id: ${occurrence.id}`);
      }
      occurrenceIds.add(occurrence.id);

      const startDate = validateJstDateTime(
        occurrence.startDate,
        `startDate for ${occurrence.id}`
      );
      if (
        Number(startDate.slice(0, 4)) < CURRENT_SCHEDULE_YEAR &&
        !event.isOver
      ) {
        throw new Error(
          `Past schedule card must be marked as over: ${event.id}`
        );
      }
      const doorTime = validateJstDateTime(
        occurrence.doorTime,
        `doorTime for ${occurrence.id}`
      );
      const endDate = occurrence.endDate === undefined
        ? null
        : validateJstDateTime(
            occurrence.endDate,
            `endDate for ${occurrence.id}`
          );
      if (doorTime.slice(0, 10) !== startDate.slice(0, 10)) {
        throw new Error(`doorTime must be on the event date for ${occurrence.id}.`);
      }
      if (Date.parse(doorTime) > Date.parse(startDate)) {
        throw new Error(`doorTime must not be after startDate for ${occurrence.id}.`);
      }
      if (endDate && Date.parse(endDate) <= Date.parse(startDate)) {
        throw new Error(`endDate must be after startDate for ${occurrence.id}.`);
      }
      if (
        previousOccurrenceStartDate &&
        previousOccurrenceStartDate >= startDate
      ) {
        throw new Error(`Occurrences must be ordered by startDate for ${event.id}.`);
      }
      previousOccurrenceStartDate = startDate;

      if (typeof occurrence.isHoliday !== "boolean") {
        throw new Error(`Invalid isHoliday for ${occurrence.id}.`);
      }
      if (occurrence.eventStatus !== "https://schema.org/EventScheduled") {
        throw new Error(`Invalid eventStatus for ${occurrence.id}.`);
      }
      if (
        occurrence.ticketUrl !== undefined ||
        occurrence.ticketUrlLabel !== undefined ||
        event.ticketOffers !== undefined
      ) {
        validateHttpsUrl(
          occurrence.ticketUrl,
          `ticket URL for ${occurrence.id}`
        );
        requireNonEmptyString(
          occurrence.ticketUrlLabel,
          `ticket URL label for ${occurrence.id}`
        );
      }
      (event.ticketOffers || []).forEach((offer) => {
        if (
          offer.validFrom &&
          Date.parse(offer.validFrom) >= Date.parse(startDate)
        ) {
          throw new Error(
            `ticket offer validFrom must be before startDate for ${occurrence.id}.`
          );
        }
      });
      validateLocation(occurrence.location, occurrence.id);

      const startTime = startDate.slice(11);
      const doorsOpenTime = doorTime.slice(11);
      const duration = endDate
        ? Date.parse(endDate) - Date.parse(startDate)
        : null;
      const location = getLocationIdentity(occurrence.location);

      if (sharedStartTime === null) {
        sharedStartTime = startTime;
        sharedDoorTime = doorsOpenTime;
        sharedDuration = duration;
        sharedLocation = location;
      } else if (
        sharedStartTime !== startTime ||
        sharedDoorTime !== doorsOpenTime ||
        sharedDuration !== duration ||
        sharedLocation !== location
      ) {
        throw new Error(
          `Grouped occurrences must share start time, door time, duration, and location: ${event.id}`
        );
      }
    });

    const cardStartDate = event.occurrences[0].startDate;
    if (previousCardStartDate && previousCardStartDate >= cardStartDate) {
      throw new Error("Schedule cards must be ordered by their first occurrence.");
    }
    previousCardStartDate = cardStartDate;
  });
};

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }

  Object.getOwnPropertyNames(value).forEach((property) => {
    deepFreeze(value[property]);
  });

  return Object.freeze(value);
};

const daiichiSeimeiHall = {
  "@type": "Place",
  name: "第一生命ホール",
  address: {
    "@type": "PostalAddress",
    streetAddress: "晴海1-8-9 晴海トリトンスクエア内",
    addressLocality: "中央区",
    addressRegion: "東京都",
    addressCountry: "JP",
  },
};

const higashiPlayhouse = {
  "@type": "Place",
  name: "東文化小劇場",
  address: {
    "@type": "PostalAddress",
    streetAddress: "大幸南1-1-10",
    addressLocality: "名古屋市東区",
    addressRegion: "愛知県",
    addressCountry: "JP",
  },
};

const musicasa = {
  "@type": "Place",
  name: "ムジカーザ",
  address: {
    "@type": "PostalAddress",
    streetAddress: "西原3-33-1",
    addressLocality: "渋谷区",
    addressRegion: "東京都",
    addressCountry: "JP",
  },
  accessNote: "「代々木上原」駅より徒歩2分",
};

const rawScheduleItems = [
  {
    id: "schedule-magic-flute-highlight-2026",
    name: "室内楽ホールdeオペラ 林美智子の『魔笛』！",
    displayTitle: "室内楽ホールdeオペラ<br>林美智子の『魔笛』！",
    displaySubtitle:
      "全アリアカット、重唱(ドイツ語/字幕付)と台詞(日本語)によるハイライト上演",
    title:
      "室内楽ホールdeオペラ<br>林美智子の『魔笛』！<br>重唱(ドイツ語/字幕付)と台詞(日本語)によるハイライト上演",
    description:
      "本多信明がタミーノ役で出演する、重唱（ドイツ語・字幕付き）と日本語の台詞による『魔笛』ハイライト公演。",
    imageKeys: ["schedule20260919_1", "schedule20260919_2"],
    organizers: [
      {
        type: "Organization",
        name: "認定NPO法人トリトン・アーツ・ネットワーク",
        url: "https://www.triton-arts.net/",
      },
      {
        type: "Organization",
        name: "第一生命ホール",
        url: "https://www.dai-ichi-seimei-hall.jp/",
      },
    ],
    ticketOffers: [
      {
        name: "S席",
        price: 7000,
        priceCurrency: "JPY",
        validFrom: "2026-02-17T10:00:00+09:00",
      },
      {
        name: "A席",
        price: 5000,
        priceCurrency: "JPY",
        validFrom: "2026-02-17T10:00:00+09:00",
      },
      {
        name: "B席",
        price: 2000,
        priceCurrency: "JPY",
        validFrom: "2026-02-17T10:00:00+09:00",
      },
      {
        name: "U25（25歳以下）",
        price: 1500,
        priceCurrency: "JPY",
        validFrom: "2026-02-17T10:00:00+09:00",
      },
    ],
    occurrences: [
      {
        id: "schedule-2026-09-19-magic-flute-highlight",
        startDate: "2026-09-19T13:30:00+09:00",
        doorTime: "2026-09-19T13:00:00+09:00",
        ticketUrl:
          "https://www.triton-arts.net/ja/concert/2026/09/19/4301/",
        ticketUrlLabel: "9月19日公演の公式チケット情報",
        isHoliday: false,
        eventStatus: "https://schema.org/EventScheduled",
        location: daiichiSeimeiHall,
      },
      {
        id: "schedule-2026-09-21-magic-flute-highlight",
        startDate: "2026-09-21T13:30:00+09:00",
        doorTime: "2026-09-21T13:00:00+09:00",
        ticketUrl:
          "https://www.triton-arts.net/ja/concert/2026/09/21/4308/",
        ticketUrlLabel: "9月21日公演の公式チケット情報",
        isHoliday: true,
        eventStatus: "https://schema.org/EventScheduled",
        location: daiichiSeimeiHall,
      },
    ],
    ticket: "S席7,000円　A席5,000円　B席2,000円　U25 1,500円(25歳以下)",
    ticketToContact: true,
    commentHtml:
      "「魔笛のタミーノ役で出演させて頂きます。皆様是非お越しください！」",
    detailsHtml:
      "【出演】<br>" +
      "<strong>タミーノ：本多信明</strong><br>" +
      "パミーナ：松原みなみ<br>" +
      "夜の女王：小川栞奈<br>" +
      "パパゲーノ：黒田博<br>" +
      "パパゲーナ：林美智子<br>" +
      "ザラストロ：後藤春馬<br>" +
      "モノスタトス：晴雅彦<br>" +
      "侍女Ⅰ＆童子Ⅰ：澤畑恵美<br>" +
      "侍女Ⅱ＆童子Ⅱ：石田滉<br>" +
      "侍女Ⅲ＆童子Ⅲ：清水華澄<br>" +
      "僧侶Ⅱ＆武士Ⅰ：西村悟<br>" +
      "ピアノ：河原忠之<br>" +
      "<br>" +
      "日本語台詞台本・構成・演出：林美智子",
    isOver: true,
  },
  {
    id: "schedule-sachia-suzuki-soprano-recital-vol-14",
    name: "鈴木さちあ ソプラノリサイタル Vol.14 歌は祈り 声は光",
    displayTitle: "鈴木さちあ ソプラノリサイタル Vol.14",
    displaySubtitle: undefined,
    title:
      "鈴木さちあ　ソプラノリサイタル Vol.14<br>歌は祈り 声は光",
    description:
      "鈴木さちあのソプラノリサイタルに本多信明がゲスト出演し、オペラアリアや『椿姫』の二重唱などを演奏します。",
    imageKeys: ["schedule20260922_1", "schedule20260922_2"],
    organizers: [
      {
        type: "Organization",
        name: "なごや歌声の会",
      },
    ],
    ticketOffers: [
      {
        name: "全席",
        price: 3000,
        priceCurrency: "JPY",
      },
    ],
    occurrences: [
      {
        id: "schedule-2026-09-22-sachia-suzuki-soprano-recital-vol-14",
        startDate: "2026-09-22T15:00:00+09:00",
        doorTime: "2026-09-22T14:30:00+09:00",
        endDate: "2026-09-22T17:00:00+09:00",
        ticketUrl: "https://sachia39.peatix.com/view",
        ticketUrlLabel: "Peatixでチケットを購入",
        isHoliday: true,
        eventStatus: "https://schema.org/EventScheduled",
        location: higashiPlayhouse,
      },
    ],
    ticket: "全席3,000円(税込)",
    ticketToContact: true,
    commentHtml:
      "「ソプラノ鈴木さちあさんのリサイタルにゲスト出演させて頂きます。オペラアリアや椿姫から二重唱などを歌わさせて頂きます。ぜひお越しください！」",
    detailsHtml:
      "【出演】<br>" +
      "ソプラノ：鈴木さちあ<br>" +
      "<strong>テノール：本多信明</strong><br>" +
      "ピアノ：森井綾子<br>" +
      "お話：川村ミチル<br>" +
      "<br>" +
      "G.ヴェルディ「椿姫」より　ある日幸せにも(二重唱)　他",
    isOver: true,
  },
  {
    id: "schedule-shigehiro-sano-memorial-concert-2026",
    name: "佐野成宏メモリアルコンサート",
    displayTitle: "佐野成宏メモリアルコンサート",
    displaySubtitle: undefined,
    title: "佐野成宏メモリアルコンサート",
    description:
      "佐野成宏氏を偲ぶメモリアルコンサートに本多信明が出演し、『ラ・ボエーム』『椿姫』『連隊の娘』のアリアなどを演奏します。",
    imageKeys: ["schedule20261212_1", "schedule20261212_2"],
    organizers: [
      {
        type: "Organization",
        name: "OperakingdomManagement",
      },
    ],
    occurrences: [
      {
        id: "schedule-2026-12-12-shigehiro-sano-memorial-concert",
        startDate: "2026-12-12T15:00:00+09:00",
        doorTime: "2026-12-12T14:30:00+09:00",
        isHoliday: false,
        eventStatus: "https://schema.org/EventScheduled",
        location: musicasa,
      },
    ],
    ticket: "全席自由5,500円",
    ticketToContact: true,
    commentHtml:
      "「12月に東京で佐野先生の追悼コンサートに出演させて頂きます。6月にムジカーザでご一緒させて頂いた大高レナさん、憧れのテノール山本康寛さん、2年前に佐野先生とのコンサートでご一緒させて頂いた泉博子さんと共演させて頂きます。佐野先生とたくさん勉強させて頂いた曲を演奏させて頂きます。皆様ぜひお越しください。」",
    detailsHtml:
      "【出演】<br>" +
      "<strong>テノール：本多信明</strong><br>" +
      "ソプラノ：大高レナ<br>" +
      "テノール：山本康寛<br>" +
      "ピアノ：泉博子<br>" +
      "<br>" +
      "オペラ「ラ・ボエーム」より　冷たき手を<br>" +
      "オペラ「椿姫」より　ああ、そは彼の人〜花から花へ<br>" +
      "オペラ「連隊の娘」より　ああ友よ、なんと素晴らしい日<br>" +
      "他",
    isOver: false,
  },
];

validateScheduleEvents(rawScheduleItems);

const scheduleItems = deepFreeze(rawScheduleItems);

module.exports = Object.freeze({
  formatScheduleLocationLabel,
  formatScheduleOccurrenceDate,
  formatScheduleTimeLabel,
  scheduleItems,
});
